import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Trash, X, Pencil } from 'lucide-react';
import { type Invoice, type Payment } from '../lib/supabase';
import { fixedInvoiceServices, fixedPaymentServices } from '../lib/fixed-invoice-payment-services';
import { formatCurrency, formatInvoiceNumber } from '../lib/formatters';

interface EnrichedPayment extends Payment {
  transactionId?: string;
}

const InvoiceDetail = () => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [payments, setPayments] = useState<EnrichedPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPayment, setCurrentPayment] = useState<Partial<Payment>>({
    payment_method: 'card',
    status: 'success',
    paid_at: new Date().toISOString().split('T')[0],
  });
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    const fetchInvoiceAndPayments = async () => {
      if (!id) {
        setError('No invoice ID provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch invoice details
        const { data: invoiceData, error: invoiceError } = await fixedInvoiceServices.getById(id);
        if (invoiceError) throw invoiceError;
        setInvoice(invoiceData);

        // Fetch related payments
        const { data: paymentsData, error: paymentsError } = await fixedPaymentServices.getByInvoice(id);
        if (paymentsError) throw paymentsError;
        setPayments(paymentsData || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch invoice details');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceAndPayments();
  }, [id]);

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Partially Paid':
        return 'bg-yellow-100 text-yellow-800';
      case 'Unpaid':
        return 'bg-orange-100 text-orange-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'Cancelled':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPaymentMethod = (method?: string) => {
    if (!method) return '-';
    switch (method.toLowerCase()) {
      case 'card':
        return 'Credit Card';
      case 'bank_transfer':
        return 'Bank Transfer';
      case 'paypal':
        return 'PayPal';
      default:
        return method;
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentPayment({
      payment_method: 'card',
      status: 'success',
      paid_at: new Date().toISOString().split('T')[0],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invoice?.id || !currentPayment.amount || !currentPayment.paid_at) {
      setError('Please fill all required fields');
      return;
    }

    try {
      // Create new payment - server will handle all validation and status updates
      const newPayment = {
        ...currentPayment,
        invoice_id: invoice.id,
        status: 'success',
      } as Payment;
      
      const result = await fixedPaymentServices.create(newPayment);
      
      if (result.error) {
        setError(result.error.message);
        return;
      }
      
      if (result.data && result.data.length > 0) {
        const createdPayment = result.data[0];
        
        // Update UI with new payment
        setPayments(prev => [createdPayment, ...prev]);

        // Refresh invoice data from server to get updated status and totals
        const { data: updatedInvoice, error: refreshError } = await fixedInvoiceServices.getById(invoice.id);
        if (refreshError) {
          setError(refreshError.message);
          return;
        }
        
        if (updatedInvoice) {
          setInvoice(updatedInvoice);
        }
      }
      
      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };

  const handleCancel = async () => {
    if (!invoice?.id) return;
    
    try {
      const result = await fixedInvoiceServices.cancel(invoice.id);
      if (result.error) throw result.error;

      // Update local state with updated invoice data
      if (result.data && result.data.length > 0) {
        setInvoice(result.data[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel invoice');
    }
  };

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="text-red-600 p-4">{error}</div>;
  if (!invoice) return <div className="text-center p-4">Invoice not found</div>;

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link to="/invoices" className="flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Invoices
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg mb-6">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Invoice #{formatInvoiceNumber(invoice)}
              </h1>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCancel}
                disabled={!invoice || invoice.status === 'Cancelled' || invoice.status === 'Paid'}
                className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium 
                  ${invoice?.status === 'Cancelled' || invoice?.status === 'Paid'
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                    : 'border-red-600 text-red-600 hover:bg-red-50'
                  }`}
                title={invoice?.status === 'Cancelled' 
                  ? 'Invoice is already cancelled'
                  : invoice?.status === 'Paid'
                    ? 'Paid invoices cannot be cancelled'
                    : 'Cancel Invoice'}
              >
                <Trash className="h-4 w-4 mr-2" /> 
                Cancel Invoice
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Amount</p>
              <p className="mt-1 text-2xl font-semibold">{formatCurrency(invoice.amount)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className="mt-1">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(invoice.status)}`}>
                  {invoice.status}
                </span>
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Due Date</p>
              <p className="mt-1">{formatDate(invoice.due_date)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Payment Date</p>
              <p className="mt-1">{invoice.paid_at ? formatDate(invoice.paid_at) : 'Unpaid'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-lg font-medium">Payment Summary</h2>
          </div>
          {invoice && invoice.status !== 'Cancelled' && invoice.status !== 'Paid' && (
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Add Payment
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-sm font-medium text-gray-500">Unpaid Balance</p>
            <p className="mt-1 text-2xl font-semibold">
              {(() => {
                const totalPaid = payments
                  .filter(p => p.status === 'success')
                  .reduce((sum, p) => sum + Number(p.amount), 0);
                return formatCurrency(Math.max(0, (invoice?.amount || 0) - totalPaid));
              })()}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Amount Paid</p>
            <p className="mt-1 text-2xl font-semibold">
              {formatCurrency(payments
                .filter(p => p.status === 'success')
                .reduce((sum, p) => sum + Number(p.amount), 0))}
            </p>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-4">Payment History</h3>
          {payments.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              No payments found for this invoice.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reference
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(payment.paid_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatPaymentMethod(payment.payment_method)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.transaction_id || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Payment Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black opacity-30" aria-hidden="true"></div>
          <div className="bg-white rounded-lg overflow-hidden shadow-xl z-50 w-full max-w-md">
            <div className="p-6">
              <h2 className="text-lg font-medium mb-4">Add Payment</h2>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                    Amount
                  </label>
                  <input
                    type="number"
                    id="amount"
                    step="0.01"
                    max={invoice ? invoice.amount - (invoice.total_paid || 0) : 0}
                    value={currentPayment.amount || ''}
                    onChange={(e) => setCurrentPayment({ ...currentPayment, amount: parseFloat(e.target.value) })}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50"
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="payment_method" className="block text-sm font-medium text-gray-700">
                    Payment Method
                  </label>
                  <select
                    id="payment_method"
                    value={currentPayment.payment_method || ''}
                    onChange={(e) => setCurrentPayment({ ...currentPayment, payment_method: e.target.value })}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50"
                  >
                    <option value="">Select a method</option>
                    <option value="card">Credit Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="paid_at" className="block text-sm font-medium text-gray-700">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    id="paid_at"
                    value={currentPayment.paid_at ? currentPayment.paid_at.split('T')[0] : ''}
                    onChange={(e) => setCurrentPayment({ ...currentPayment, paid_at: e.target.value })}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="transaction_id" className="block text-sm font-medium text-gray-700">
                    Transaction Reference
                  </label>
                  <input
                    type="text"
                    id="transaction_id"
                    value={currentPayment.transaction_id || ''}
                    onChange={(e) => setCurrentPayment({ ...currentPayment, transaction_id: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-opacity-50"
                  />
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Save Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail;
